import { useEffect } from 'react';
import { useAppStore } from '@/store/appStore';
import { X, CheckCircle2, AlertCircle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  undoId?: string;
}

export function ToastContainer() {
  const toasts = useAppStore((state) => state.toasts);
  const removeToast = useAppStore((state) => state.removeToast);

  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
}

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      // Clear undo action when toast expires
      if (toast.undoId) {
        useAppStore.getState().clearUndoAction(toast.undoId);
      }
      onClose();
    }, toast.duration || 4000);

    return () => {
      clearTimeout(timer);
      // Clear undo action when toast is manually closed
      if (toast.undoId) {
        useAppStore.getState().clearUndoAction(toast.undoId);
      }
    };
  }, [toast.duration, toast.undoId, onClose]);

  const icons = {
    success: <CheckCircle2 className="w-4 h-4 text-accent-emerald flex-shrink-0" />,
    error: <AlertCircle className="w-4 h-4 text-accent-rose flex-shrink-0" />,
    info: <Info className="w-4 h-4 text-accent-blue flex-shrink-0" />,
  };

  const bgColors = {
    success: 'bg-accent-emerald-dim border-accent-emerald',
    error: 'bg-accent-rose-dim border-accent-rose',
    info: 'bg-accent-blue-dim border-accent-blue',
  };

  const textColors = {
    success: 'text-text-primary',
    error: 'text-text-primary',
    info: 'text-text-primary',
  };

  return (
    <div
      className={`
        pointer-events-auto
        flex items-start gap-3 
        px-4 py-3 rounded-lg 
        border shadow-lg
        backdrop-blur-sm
        animate-in slide-in-from-right-full duration-300
        min-w-[320px] max-w-md
        ${bgColors[toast.type]}
      `}
    >
      {icons[toast.type]}
      <p className={`flex-1 text-sm font-medium ${textColors[toast.type]}`}>
        {toast.message}
      </p>
      {toast.action && (
        <button
          onClick={toast.action.onClick}
          className={`
            px-3 py-1 text-xs font-semibold rounded
            hover:bg-black/10 dark:hover:bg-white/10
            transition-colors flex-shrink-0
            ${textColors[toast.type]}
          `}
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={() => {
          // Clear undo action when manually closed via X button
          if (toast.undoId) {
            useAppStore.getState().clearUndoAction(toast.undoId);
          }
          onClose();
        }}
        className={`
          p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5
          transition-colors flex-shrink-0
          ${textColors[toast.type]}
        `}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

